import m from 'mithril';

import { changePassword, logOut } from '../services/auth';
import {
  getAuthState,
  refreshAuthProfile,
} from '../services/authStore';
import { updateAuthProfile } from '../services/profile';
import { captureException } from '../services/sentry';
import {
  hasNoErrors,
  validatePasswordChange,
  type FormErrors,
  type PasswordChangeValues,
} from '../services/forms';
import { initials } from '../utils/initials';
import { Alert } from '../components/Alert';
import { AvatarPicker } from '../components/AvatarPicker';
import { Button } from '../components/Button';
import { ColorField, TextField } from '../components/FormControls';
import { Loading } from '../components/Loading';
import { Type } from '../components/Typography';
import heroBanner from '../assets/images/AccountMain/heroBanner.svg';
import type { User } from '../types';

/**
 * Account profile form values.
 */
export interface AccountProfileValues {
  /** Full display name. */
  displayName: string;
  /** Fallback avatar color. */
  color: string;
  /** Profile image URL, data URL, or null removal marker. */
  image?: string | null;
}

/**
 * Account management page.
 */
export const Account: m.ClosureComponent = () => {
  const state = {
    profileId: '',
    profile: {
      displayName: '',
      color: '#2563eb',
      image: null as string | null,
    },
    password: {
      oldPass: '',
      newPass: '',
      confirmPass: '',
    } satisfies PasswordChangeValues,
    passwordErrors: {} as FormErrors<PasswordChangeValues>,
    savingProfile: false,
    savingPassword: false,
    message: '',
    error: '',
  };

  const syncProfile = (profile: User): void => {
    if (state.profileId === profile.id) {
      return;
    }

    state.profileId = profile.id;
    state.profile = {
      displayName: profile.displayName,
      color: profile.color,
      image: profile.image ?? null,
    };
  };

  const saveProfile = (profile: User): void => {
    state.savingProfile = true;
    state.error = '';
    state.message = '';
    void updateAuthProfile(profile, {
      displayName: state.profile.displayName,
      color: state.profile.color,
      image: state.profile.image,
    })
      .then(async () => {
        await refreshAuthProfile();
        state.message = 'Your profile changes have been saved.';
      })
      .catch((error: unknown) => {
        captureException(error);
        console.error('profile save failed:', error);
        state.error = 'Uh oh, your profile changes failed to save.';
      })
      .finally(() => {
        state.savingProfile = false;
        m.redraw();
      });
  };

  const savePassword = (): void => {
    state.passwordErrors = validatePasswordChange(state.password);
    state.error = '';
    state.message = '';
    if (!hasNoErrors(state.passwordErrors)) {
      return;
    }

    state.savingPassword = true;
    void changePassword(state.password.oldPass, state.password.newPass)
      .then(() => {
        state.password = {
          oldPass: '',
          newPass: '',
          confirmPass: '',
        };
        state.message = 'Password changed.';
      })
      .catch((error: unknown) => {
        captureException(error);
        console.error('password change failed:', error);
        state.error = 'Uh oh, your password could not be changed. Please wait a moment and try again.';
      })
      .finally(() => {
        state.savingPassword = false;
        m.redraw();
      });
  };

  return {
    view: () => {
      const auth = getAuthState();
      const loading = auth.authLoading || auth.profileLoading;

      if (loading) {
        return m('section', { class: 'page page--centered' }, m(Loading, { large: true }));
      }

      if (!auth.profile) {
        m.route.set('/login/account');
        return null;
      }

      syncProfile(auth.profile);
      const currentInitials = initials(state.profile.displayName);

      return m('section', { class: 'account-page' }, [
        m('section', { class: 'hero-strip' }, [
          m('div', { class: 'hero-strip__copy' }, [
            m(Type, { size: 'title' }, 'Account Management'),
            m(Type, { size: 'subtitle' }, 'Change your MeshOS account details here.'),
          ]),
          m('img', { src: heroBanner, alt: '', class: 'hero-strip__image' }),
        ]),
        m('section', { class: 'page account-page__body' }, [
          state.message ? m(Alert, { variant: 'success', message: state.message }) : null,
          state.error ? m(Alert, { variant: 'danger', message: state.error }) : null,
          m(Type, { size: 'heading' }, 'My Profile'),
          m('div', { class: 'account-grid' }, [
            m('form', {
              class: 'profile-form',
              onsubmit: (event: SubmitEvent) => {
                event.preventDefault();
                saveProfile(auth.profile as User);
              },
            }, [
              m('div', { class: 'profile-form__avatar', style: { backgroundColor: state.profile.color } }, [
                m(AvatarPicker, {
                  value: state.profile.image,
                  displayName: state.profile.displayName,
                  color: state.profile.color,
                  onChange: (value) => {
                    state.profile.image = value;
                  },
                }),
              ]),
              m('div', { class: 'profile-form__fields' }, [
                m('div', { class: 'profile-form__name-row' }, [
                  m(TextField, {
                    label: 'Full Name',
                    name: 'displayName',
                    placeholder: 'Full Name',
                    value: state.profile.displayName,
                    onInput: (value) => {
                      state.profile.displayName = value;
                    },
                  }),
                  m('div', {
                    class: 'initials-preview',
                    style: { backgroundColor: state.profile.color },
                    id: 'initials',
                  }, currentInitials),
                ]),
                m(ColorField, {
                  label: 'Profile Color',
                  value: state.profile.color,
                  hint: 'This color will be used when your profile photo cannot be shown.',
                  onChange: (value) => {
                    state.profile.color = value;
                    m.redraw();
                  },
                }),
                m(TextField, {
                  label: 'Email',
                  name: 'email',
                  type: 'email',
                  disabled: true,
                  value: auth.profile.email ?? '',
                  onInput: () => undefined,
                }),
                m('div', { class: 'form-actions form-actions--right profile-form__actions' }, [
                  m(Button, {
                    type: 'submit',
                    loading: state.savingProfile,
                    disabled: state.savingProfile,
                  }, 'Save Changes'),
                ]),
              ]),
            ]),
            m('form', {
              class: 'password-form stacked-form',
              onsubmit: (event: SubmitEvent) => {
                event.preventDefault();
                savePassword();
              },
            }, [
              m(TextField, {
                label: 'Current Password',
                name: 'oldPass',
                type: 'password',
                value: state.password.oldPass,
                hint: state.passwordErrors.oldPass,
                onInput: (value) => {
                  state.password.oldPass = value;
                },
              }),
              m(TextField, {
                label: 'New Password',
                name: 'newPass',
                type: 'password',
                value: state.password.newPass,
                hint: state.passwordErrors.newPass,
                onInput: (value) => {
                  state.password.newPass = value;
                },
              }),
              m(TextField, {
                label: 'Confirm New Password',
                name: 'confirmPass',
                type: 'password',
                value: state.password.confirmPass,
                hint: state.passwordErrors.confirmPass,
                onInput: (value) => {
                  state.password.confirmPass = value;
                },
              }),
              m('div', { class: 'form-actions form-actions--right' }, [
                m(Button, {
                  type: 'submit',
                  loading: state.savingPassword,
                  disabled: state.savingPassword,
                }, 'Change Password'),
              ]),
            ]),
          ]),
          m('hr'),
          m(Button, {
            appearance: 'outlined',
            variant: 'neutral',
            onclick: (event) => {
              event.redraw = false;
              void logOut().then(() => {
                m.route.set('/');
              });
            },
          }, 'Log Out'),
        ]),
      ]);
    },
  };
};
